import React, { useEffect, useRef } from 'react';

import { StepProps, track, useAutoNavigate, useMutation } from '@betterment/js-runtime';
import { Text } from '@betterment/samba-react';
import { Box, Button, Heading } from '@betterment/style-closet-react';

import { CustomerSignupValues } from './Controller';

import { CustomerCreateParams, CustomerCreateResponse, PrimaryIdStatus } from '../Checking/data';
import { isManualReview, isVerified } from '../Checking/models/customer';
import FullPageLoader from '../Shared/components/FullPageLoader';
import { WorkflowLayout } from '../Shared/layout/WorkflowLayout';
import { locationAssign } from '../Shared/path-helpers';
import { urlBuilder } from '../Shared/urlBuilder';

interface Props extends StepProps<CustomerSignupValues> {
  handleClose: () => void;
}

const BeginVerification: React.FC<Props> = ({ flowState, goToNextStep, goToPreviousStep, handleClose }) => {
  const modalHeader = useRef<HTMLSpanElement>(null);
  const navigateRouter = useAutoNavigate();
  const [createCustomer, { loading: isLoadingCreateCustomer }] = useMutation<
    CustomerCreateResponse,
    CustomerCreateParams
  >(urlBuilder.postCheckingCustomerCreation.absolute());

  useEffect(() => {
    track('ElementViewed', { name: 'IdentityVerificationOverview', flow: 'OpenCheckingAccount' });
  }, []);

  useEffect(() => {
    if (modalHeader.current) {
      modalHeader.current.scrollIntoView();
    }
  }, [modalHeader]);

  const { customer, stepTitle } = flowState.values;

  if (customer && (isVerified(customer) || isManualReview(customer))) {
    locationAssign(urlBuilder.appHome.absolute());
    return <FullPageLoader />;
  }

  const submitCustomerCreation = async () => {
    track('ElementClicked', { name: 'ContinueCheckingSignup4' });
    const { data, errors } = await createCustomer(flowState.values);

    if (errors) return goToNextStep({ nextStep: 'limit_error' });

    if (!data) return null;

    if (data.nextStep === 'retry') return goToNextStep({ nextStep: 'limit_error' });

    if (data.nextStep === 'manual_review') {
      if (data.primaryIdStatus === PrimaryIdStatus.Present) {
        return locationAssign(urlBuilder.getCheckingMultidocUploadsSuccess.absolute());
      }
      return locationAssign(urlBuilder.getCheckingMultidocUploadPage.absolute());
    }

    if (data.nextStep === 'success' && data.invitationId) {
      return navigateRouter(
        urlBuilder.getAppCheckingJointInvitationAcceptanceSuccess.absolute({
          jointAccountInvitationId: data.invitationId,
          displaySuccessPage: true,
        })
      );
    }

    return goToNextStep({ nextStep: data.nextStep });
  };

  return (
    <WorkflowLayout
      pageTitle="Sign up - Checking"
      workflowTitle={stepTitle}
      onBack={goToPreviousStep}
      onClose={handleClose}
    >
      <Box>
        <Heading variant="primary">Verify your identity</Heading>
        <Box my="8">
          <Text as="p" css={{ marginBottom: 'space.200', _last: { marginBottom: 'space.0' } }}>
            We'll next run some required verification based on the information you've provided. You may be asked for
            additional documentation based on our automated review.
          </Text>
        </Box>
        <Button type="submit" disabled={isLoadingCreateCustomer} onClick={submitCustomerCreation}>
          Continue
        </Button>
      </Box>
    </WorkflowLayout>
  );
};
export default BeginVerification;
